import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Calendar, Heart, Copy, Check, Camera, ChevronDown } from 'lucide-react';

// Vídeo de fundo do hero — adicione o arquivo em public/videos/hero.mp4 e preencha o caminho abaixo.
// Enquanto estiver vazio, o gradiente atual continua sendo exibido normalmente.
const HERO_VIDEO_SRC = '';

// Fotos da galeria — troque `src: null` por '/gallery/nome-do-arquivo.jpg' (colocando o arquivo em frontend/public/gallery/) quando tiver as fotos reais.
const GALLERY_PHOTOS = [
  { caption: 'Ceia do Senhor', src: '/gallery/ceia.jpg' },
  { caption: 'Células nos Lares', src: '/gallery/celula.jpg' },
  { caption: 'Momentos de Comunhão', src: '/gallery/comunhao.jpg' },
  { caption: 'Treinamento de Líderes', src: '/gallery/treinamento.jpg' },
  { caption: 'Oração e Bênção', src: '/gallery/oracao.jpg' },
  { caption: 'Encontros nos Lares', src: '/gallery/lares.jpg' },
];

const PhotoPlaceholder = ({ caption, icon: Icon = Camera }) => (
  <div style={{
    height: '100%', width: '100%',
    background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '8px', color: '#93c5fd', padding: '12px', boxSizing: 'border-box',
  }}>
    <Icon size={28} strokeWidth={1.5} />
    {caption && (
      <span style={{ fontSize: '11px', fontWeight: 600, color: '#60a5fa', textAlign: 'center' }}>{caption}</span>
    )}
  </div>
);

const MESES = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'];
const DIAS_SEMANA = ['domingo','segunda-feira','terça-feira','quarta-feira','quinta-feira','sexta-feira','sábado'];
const DIAS_LABEL = {
  domingo: 'Domingo', segunda: 'Segunda', terça: 'Terça',
  quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sábado: 'Sábado',
};

const TYPE_LABEL = {
  'culto': { label: 'Culto', color: '#1d4ed8', bg: '#eff6ff' },
  'reuniao': { label: 'Reunião', color: '#6d28d9', bg: '#f5f3ff' },
  'evento-especial': { label: 'Especial', color: '#b45309', bg: '#fffbeb' },
  'estudo': { label: 'Estudo', color: '#059669', bg: '#f0fdf4' },
  'outro': { label: 'Outro', color: '#475569', bg: '#f1f5f9' },
};

const LandingPage = () => {
  const [copied, setCopied] = useState(false);
  const [events, setEvents] = useState([]);
  const [showOracaoModal, setShowOracaoModal] = useState(false);
  const [oracaoForm, setOracaoForm] = useState({ nome: '', pedido: '' });
  const [oracaoStatus, setOracaoStatus] = useState(null);

  const [celulasPublicas, setCelulasPublicas] = useState([]);
  const [bairroFiltro, setBairroFiltro] = useState('todos');
  const [showCelulasModal, setShowCelulasModal] = useState(false);
  const [showInteresseModal, setShowInteresseModal] = useState(false);
  const [interesseForm, setInteresseForm] = useState({ nome: '', telefone: '', mensagem: '' });
  const [interesseCelula, setInteresseCelula] = useState(null);
  const [interesseStatus, setInteresseStatus] = useState(null);

  useEffect(() => {
    document.title = 'Comunidade Evangélica Apascentar | Curitiba';
  }, []);

  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        raf = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    fetch('/api/events/public/upcoming?limit=4')
      .then(r => r.json())
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch('/api/celulas/public')
      .then(r => r.json())
      .then(data => setCelulasPublicas(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleEnviarInteresse = async (e) => {
    e.preventDefault();
    if (!interesseForm.nome || !interesseForm.telefone) return;
    setInteresseStatus('sending');
    try {
      await fetch('/api/celulas/interesse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: interesseForm.nome,
          telefone: interesseForm.telefone,
          mensagem: interesseForm.mensagem,
          celulaId: interesseCelula?._id || null,
          celulaNome: interesseCelula?.nome || '',
          bairroDesejado: interesseCelula?.bairro || ''
        }),
      });
      setInteresseStatus('success');
      setInteresseForm({ nome: '', telefone: '', mensagem: '' });
      setTimeout(() => { setShowInteresseModal(false); setInteresseStatus(null); }, 2500);
    } catch {
      setInteresseStatus('error');
    }
  };

  const bairros = ['todos', ...Array.from(new Set(celulasPublicas.map(c => c.bairro).filter(Boolean)))];
  const celulasFiltradas = bairroFiltro === 'todos' ? celulasPublicas : celulasPublicas.filter(c => c.bairro === bairroFiltro);

  const copyPix = () => {
    navigator.clipboard.writeText('63.537.026/0001-93');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleEnviarOracao = async (e) => {
    e.preventDefault();
    if (!oracaoForm.pedido.trim()) return;
    setOracaoStatus('sending');
    try {
      await fetch('/api/pedidos-oracao', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: oracaoForm.nome || 'Anônimo', pedido: oracaoForm.pedido }),
      });
      setOracaoStatus('success');
      setOracaoForm({ nome: '', pedido: '' });
      setTimeout(() => { setShowOracaoModal(false); setOracaoStatus(null); }, 2500);
    } catch {
      setOracaoStatus('error');
    }
  };

  const meetings = [
    {
      title: 'Ceia do Senhor',
      sub: 'com Ceia Ágape',
      schedule: 'Todo 1º domingo do mês',
      time: '9h da manhã',
      emoji: '🍞',
      color: '#6d28d9',
      bg: '#f5f3ff',
      border: '#ddd6fe',
    },
    {
      title: 'Culto de Celebração',
      sub: null,
      schedule: 'Todos os domingos',
      time: '18h',
      emoji: '✝',
      color: '#1d4ed8',
      bg: '#eff6ff',
      border: '#bfdbfe',
    },
    {
      title: 'Células nos Lares',
      sub: null,
      schedule: 'Durante a semana',
      time: '19h',
      emoji: '🏠',
      color: '#059669',
      bg: '#f0fdf4',
      border: '#a7f3d0',
    },
  ];

  const pixInfo = [
    { label: 'CNPJ / PIX', value: '63.537.026/0001-93' },
    { label: 'Banco', value: 'Cora' },
    { label: 'Conta', value: '6468073-2' },
    { label: 'Favorecido', value: 'Comunidade Evangélica Apascentar de Curitiba' },
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1e293b', background: '#ffffff', minHeight: '100vh' }}>

      {/* NAVBAR */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #e2e8f0',
        padding: '0 16px',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '56px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/apas.png" alt="Apascentar" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            <span style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b', whiteSpace: 'nowrap' }}>Apascentar</span>
            <span className="nav-link-hide" style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '400' }}>· Curitiba</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <a href="#encontros" className="nav-link-hide" style={{ fontSize: '13px', color: '#64748b', textDecoration: 'none', fontWeight: '500' }}>Encontros</a>
            <a href="#celulas" className="nav-link-hide" style={{ fontSize: '13px', color: '#64748b', textDecoration: 'none', fontWeight: '500' }}>Células</a>
            <a href="#localizacao" className="nav-link-hide" style={{ fontSize: '13px', color: '#64748b', textDecoration: 'none', fontWeight: '500' }}>Localização</a>
            <a href="#contribuicoes" className="nav-link-hide" style={{ fontSize: '13px', color: '#64748b', textDecoration: 'none', fontWeight: '500' }}>Contribuições</a>
            <Link
              to="/login"
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: '#ffffff', borderRadius: '8px',
                textDecoration: 'none', fontSize: '13px', fontWeight: '600',
                boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                whiteSpace: 'nowrap',
              }}
            >
              <span className="nav-link-hide">Área do </span>Membro
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section style={{
        backgroundImage: `
          radial-gradient(circle at 1px 1px, rgba(255,255,255,0.09) 1px, transparent 0),
          radial-gradient(circle at 22% 12%, rgba(96,165,250,0.35), transparent 55%),
          linear-gradient(180deg, rgba(15,23,42,0.08) 0%, rgba(15,23,42,0.55) 100%),
          linear-gradient(160deg, #0f1e4d 0%, #1e3a8a 35%, #1d4ed8 70%, #2563eb 100%)
        `,
        backgroundSize: '26px 26px, auto, auto, auto',
        padding: '110px 24px 96px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {HERO_VIDEO_SRC && (
          <>
            <video autoPlay muted loop playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
              <source src={HERO_VIDEO_SRC} type="video/mp4" />
            </video>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(15,23,42,0.82), rgba(29,78,216,0.75))', zIndex: 1 }} />
          </>
        )}
        <div style={{ position: 'absolute', width: '560px', height: '560px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.06), transparent 70%)', top: '-180px', right: '-180px', pointerEvents: 'none', transform: `translateY(${scrollY * 0.15}px)` }} />
        <div style={{ position: 'absolute', width: '380px', height: '380px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05), transparent 70%)', bottom: '-120px', left: '-120px', pointerEvents: 'none', transform: `translateY(${scrollY * -0.1}px)` }} />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: '640px', margin: '0 auto' }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: 'clamp(34px, 6vw, 58px)', fontWeight: '700', color: '#ffffff', margin: '0 0 18px', letterSpacing: '-0.5px', lineHeight: '1.15', textShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
            Bem-vindo à Apascentar
          </h1>

          <div style={{ width: '36px', height: '2px', background: 'rgba(255,255,255,0.35)', margin: '0 auto 22px' }} />

          <p style={{ fontSize: '20px', fontStyle: 'italic', color: 'rgba(255,255,255,0.94)', lineHeight: '1.7', margin: '0 0 8px', maxWidth: '460px', marginLeft: 'auto', marginRight: 'auto' }}>
            "Apascenta minhas ovelhas."
          </p>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', margin: '0 0 52px', letterSpacing: '0.3px' }}>João 21:17</p>

          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="#encontros" className="btn-hover" style={{
              padding: '14px 30px',
              background: 'rgba(255,255,255,0.1)',
              color: '#ffffff', borderRadius: '12px',
              textDecoration: 'none', fontSize: '14px', fontWeight: '600',
              border: '1.5px solid rgba(255,255,255,0.32)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.15)',
            }}>
              Nossos Encontros
            </a>
            <a href="#localizacao" className="btn-hover" style={{
              padding: '14px 30px',
              background: '#ffffff',
              color: '#1d4ed8', borderRadius: '12px',
              textDecoration: 'none', fontSize: '14px', fontWeight: '700',
              boxShadow: '0 10px 28px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.15)',
            }}>
              Como Chegar
            </a>
            <button
              onClick={() => setShowOracaoModal(true)}
              className="btn-hover"
              style={{
                padding: '14px 30px',
                background: 'rgba(255,255,255,0.1)',
                color: '#ffffff', borderRadius: '12px',
                border: '1.5px solid rgba(255,255,255,0.32)',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              🙏 Pedir Oração
            </button>
          </div>
        </div>

        <div className="scroll-bounce" style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 2, opacity: 0.55, pointerEvents: 'none' }}>
          <ChevronDown size={22} color="#fff" />
        </div>
      </section>

      {/* SOBRE */}
      <section style={{ padding: '80px 24px', background: '#f8fafc' }}>
        <div className="sobre-grid" style={{ maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'center' }}>
          <div style={{ textAlign: 'left' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#3b82f6', letterSpacing: '2px', textTransform: 'uppercase' }}>Quem Somos</span>
            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: '12px 0 28px', letterSpacing: '-0.5px' }}>
              Sobre Nós
            </h2>
            <p style={{ fontSize: '17px', color: '#475569', lineHeight: '1.9', margin: 0 }}>
              Vivemos o versículo de Jesus com Pedro: <em>"Apascenta minhas ovelhas."</em> Estamos aqui para trilhar o caminho juntos, aperfeiçoando uns aos outros pela luz da Palavra. Queremos ser uma fonte de apoio e inspiração, ajudando cada um a encontrar seu propósito e caminhar com esperança e alegria.
            </p>
          </div>
          <div className="hover-lift" style={{ borderRadius: '24px', overflow: 'hidden', aspectRatio: '4 / 3', boxShadow: '0 12px 32px rgba(0,0,0,0.1)' }}>
            <img src="/gallery/comunidade.jpg" alt="Nossa Comunidade" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>
      </section>

      {/* GALERIA */}
      <section style={{ padding: '80px 24px', background: '#ffffff' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#3b82f6', letterSpacing: '2px', textTransform: 'uppercase' }}>Nossos Momentos</span>
            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: '12px 0 0', letterSpacing: '-0.5px' }}>
              Galeria
            </h2>
          </div>
          <div className="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {GALLERY_PHOTOS.map((photo, i) => (
              <div key={i} className="hover-lift" style={{ borderRadius: '16px', overflow: 'hidden', aspectRatio: '4 / 3', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                {photo.src
                  ? <img src={photo.src} alt={photo.caption} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <PhotoPlaceholder caption={photo.caption} />}
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* PRÓXIMOS EVENTOS */}
      {events.length > 0 && (
        <section style={{ padding: '80px 24px', background: '#ffffff' }}>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#3b82f6', letterSpacing: '2px', textTransform: 'uppercase' }}>Na agenda</span>
              <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: '12px 0 0', letterSpacing: '-0.5px' }}>
                Próximos Eventos
              </h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {events.map(ev => {
                const date = new Date(ev.date);
                const tipo = TYPE_LABEL[ev.type] || TYPE_LABEL['outro'];
                return (
                  <div key={ev._id} style={{
                    display: 'flex', alignItems: 'center', gap: '16px',
                    background: '#f8fafc', borderRadius: '16px', padding: '16px 20px',
                    border: '1px solid #e2e8f0',
                    transition: 'box-shadow 0.2s',
                  }}
                    onMouseOver={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.07)'; }}
                    onMouseOut={e => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    {/* Data */}
                    <div style={{
                      minWidth: '52px', textAlign: 'center',
                      background: '#ffffff', borderRadius: '12px',
                      padding: '8px 4px', border: '1px solid #e2e8f0',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    }}>
                      <div style={{ fontSize: '22px', fontWeight: '800', color: '#1d4ed8', lineHeight: '1' }}>
                        {String(date.getDate()).padStart(2, '0')}
                      </div>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.5px' }}>
                        {MESES[date.getMonth()]}
                      </div>
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{ev.title}</span>
                        <span style={{
                          fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '6px',
                          color: tipo.color, background: tipo.bg,
                        }}>{tipo.label}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <Clock size={12} style={{ color: '#94a3b8' }} />
                        <span style={{ fontSize: '13px', color: '#64748b' }}>
                          {ev.time} · {DIAS_SEMANA[date.getDay()]}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ENCONTROS */}
      <section id="encontros" style={{ padding: '80px 24px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '52px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#3b82f6', letterSpacing: '2px', textTransform: 'uppercase' }}>Venha nos visitar</span>
            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: '12px 0 0', letterSpacing: '-0.5px' }}>
              Encontros do Mês
            </h2>
          </div>

          <div className="meetings-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {meetings.map((m, i) => (
              <div
                key={i}
                style={{ background: m.bg, borderRadius: '20px', padding: '32px 24px', border: `1px solid ${m.border}`, transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'default' }}
                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)'; }}
                onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ fontSize: '36px', marginBottom: '20px' }}>{m.emoji}</div>
                <h3 style={{ fontSize: '18px', fontWeight: '800', color: m.color, margin: '0 0 4px' }}>{m.title}</h3>
                {m.sub && <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px', fontWeight: '500' }}>{m.sub}</p>}
                <div style={{ marginTop: m.sub ? '0' : '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <Calendar size={13} style={{ color: '#94a3b8' }} />
                    <span style={{ fontSize: '13px', color: '#64748b' }}>{m.schedule}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Clock size={13} style={{ color: '#94a3b8' }} />
                    <span style={{ fontSize: '16px', fontWeight: '700', color: m.color }}>{m.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CÉLULAS */}
      <section id="celulas" style={{ padding: '0 0 80px', background: '#ffffff' }}>
        {/* Banner hero escuro */}
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 55%, #1e3a8a 100%)',
          padding: '72px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden',
          marginBottom: '0',
        }}>
          <div style={{ position: 'absolute', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(99,102,241,0.12)', top: '-100px', right: '-80px', pointerEvents: 'none', transform: `translateY(${scrollY * 0.08}px)` }} />
          <div style={{ position: 'absolute', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(59,130,246,0.1)', bottom: '-80px', left: '-60px', pointerEvents: 'none', transform: `translateY(${scrollY * -0.06}px)` }} />
          <div style={{ position: 'relative', maxWidth: '640px', margin: '0 auto' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: 'rgba(165,180,252,0.8)', letterSpacing: '2px', textTransform: 'uppercase' }}>Vivemos em comunidade</span>
            <h2 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: '800', color: '#ffffff', margin: '12px 0 16px', lineHeight: '1.1', letterSpacing: '-0.5px' }}>
              Somos uma Igreja<br />em Células.
            </h2>
            <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.65)', margin: '0 0 32px', lineHeight: '1.7' }}>
              Nos reunimos nas casas para <strong style={{ color: 'rgba(255,255,255,0.9)' }}>edificação e comunhão</strong>.<br />
              Encontre uma célula perto de você.
            </p>
            <button onClick={() => { setBairroFiltro('todos'); setShowCelulasModal(true); }} className="btn-hover" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '14px 32px', background: '#ffffff', color: '#1e1b4b',
              borderRadius: '50px', border: 'none', fontSize: '15px', fontWeight: '700',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)', cursor: 'pointer',
            }}>
              🏠 Clique aqui e encontre uma célula!
            </button>
          </div>
        </div>
      </section>

      {/* MODAL CÉLULAS */}
      {showCelulasModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target === e.currentTarget) setShowCelulasModal(false); }}>
          <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>🏠 Encontre uma Célula</h2>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>Escolha uma célula perto de você</p>
              </div>
              <button onClick={() => setShowCelulasModal(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
            </div>

            {/* Filtro por bairro */}
            {bairros.length > 2 && (
              <div style={{ padding: '14px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '8px', flexWrap: 'wrap', flexShrink: 0 }}>
                {bairros.map(b => (
                  <button key={b} onClick={() => setBairroFiltro(b)} style={{
                    padding: '6px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                    fontSize: '12px', fontWeight: '600',
                    background: bairroFiltro === b ? '#1e3a8a' : '#f1f5f9',
                    color: bairroFiltro === b ? '#fff' : '#475569',
                  }}>
                    {b === 'todos' ? 'Todos' : b}
                  </button>
                ))}
              </div>
            )}

            {/* Lista */}
            <div style={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
              {celulasPublicas.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8' }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>🏠</div>
                  <p style={{ fontSize: '15px', marginBottom: '16px' }}>Células em breve na sua região.</p>
                  <button onClick={() => { setShowCelulasModal(false); setInteresseCelula(null); setShowInteresseModal(true); }} style={{
                    padding: '10px 24px', background: 'linear-gradient(135deg, #1e3a8a, #1d4ed8)',
                    color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '600',
                  }}>
                    Quero ser avisado
                  </button>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                  {celulasFiltradas.map(c => (
                    <div key={c._id} style={{ background: '#f8fafc', borderRadius: '14px', padding: '20px', border: '1px solid #e2e8f0' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', marginBottom: '12px' }}>🏠</div>
                      <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: '0 0 10px' }}>{c.nome}</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '16px' }}>
                        {c.bairro && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b' }}>
                            <span>📍</span><span>{c.bairro}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b' }}>
                          <span>📅</span>
                          <span style={{ textTransform: 'capitalize' }}>{DIAS_LABEL[c.diaSemana] || c.diaSemana}s às {c.horario}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b' }}>
                          <span>👤</span><span>Líder: {c.liderNome}</span>
                        </div>
                      </div>
                      <button onClick={() => { setShowCelulasModal(false); setInteresseCelula(c); setShowInteresseModal(true); }} style={{
                        width: '100%', padding: '9px',
                        background: 'linear-gradient(135deg, #1e3a8a, #1d4ed8)',
                        color: '#fff', border: 'none', borderRadius: '9px',
                        fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      }}>
                        Quero participar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL INTERESSE CÉLULA */}
      {showInteresseModal && (
        <div onClick={() => { setShowInteresseModal(false); setInteresseStatus(null); }} style={{
          position: 'fixed', inset: 0, zIndex: 1001,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#ffffff', borderRadius: '20px',
            padding: '32px', width: '100%', maxWidth: '440px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
          }}>
            {interesseStatus === 'success' ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b', margin: '0 0 8px' }}>Interesse registrado!</h2>
                <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                  Em breve entraremos em contato. Deus abençoe!
                </p>
              </div>
            ) : (
              <form onSubmit={handleEnviarInteresse}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
                      🏠 {interesseCelula ? interesseCelula.nome : 'Encontre uma Célula'}
                    </h2>
                    {interesseCelula?.bairro && (
                      <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>📍 {interesseCelula.bairro}</p>
                    )}
                  </div>
                  <button type="button" onClick={() => { setShowInteresseModal(false); setInteresseStatus(null); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#94a3b8' }}>×</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Nome *</label>
                    <input type="text" required placeholder="Seu nome completo" value={interesseForm.nome}
                      onChange={e => setInteresseForm(p => ({ ...p, nome: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Telefone (WhatsApp) *</label>
                    <input type="tel" required placeholder="(00) 00000-0000" value={interesseForm.telefone}
                      onChange={e => setInteresseForm(p => ({ ...p, telefone: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Mensagem <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span></label>
                    <textarea placeholder="Conte um pouco sobre você..." value={interesseForm.mensagem} rows={3}
                      onChange={e => setInteresseForm(p => ({ ...p, mensagem: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  </div>
                </div>
                {interesseStatus === 'error' && (
                  <p style={{ color: '#ef4444', fontSize: '13px', margin: '12px 0 0' }}>Erro ao enviar. Tente novamente.</p>
                )}
                <button type="submit" disabled={interesseStatus === 'sending'} style={{
                  width: '100%', marginTop: '20px', padding: '13px',
                  background: 'linear-gradient(135deg, #1e3a8a, #1d4ed8)',
                  color: '#ffffff', border: 'none', borderRadius: '10px',
                  fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(30,58,138,0.3)',
                }}>
                  {interesseStatus === 'sending' ? 'Enviando...' : 'Enviar interesse'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* LOCALIZAÇÃO */}
      <section id="localizacao" style={{ padding: '80px 24px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '52px' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#3b82f6', letterSpacing: '2px', textTransform: 'uppercase' }}>Onde Estamos</span>
            <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: '12px 0 0', letterSpacing: '-0.5px' }}>
              Como Chegar
            </h2>
          </div>

          <div className="location-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '48px', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '28px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <MapPin size={22} style={{ color: '#3b82f6' }} />
                </div>
                <div>
                  <p style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Endereço</p>
                  <p style={{ fontSize: '16px', color: '#1e293b', margin: 0, lineHeight: '1.7', fontWeight: '500' }}>
                    Rua Atílio Brunetti, 2014<br />
                    Capão Raso, Curitiba – PR<br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>CEP 81150-070</span>
                  </p>
                </div>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Rua+At%C3%ADlio+Brunetti,+2014,+Cap%C3%A3o+Raso,+Curitiba,+PR"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hover"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '11px 22px',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: '#ffffff', borderRadius: '10px',
                  textDecoration: 'none', fontSize: '13px', fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
                }}
              >
                <MapPin size={14} />
                Abrir no Google Maps
              </a>
            </div>

            <div style={{ borderRadius: '20px', overflow: 'hidden', height: '320px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
              <iframe
                title="Localização Igreja Apascentar"
                src="https://maps.google.com/maps?q=Rua+At%C3%ADlio+Brunetti%2C+2014%2C+Cap%C3%A3o+Raso%2C+Curitiba%2C+PR%2C+81150-070&output=embed&hl=pt-BR"
                width="100%"
                height="320"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CONTRIBUIÇÕES */}
      <section id="contribuicoes" style={{ padding: '80px 24px', background: '#ffffff' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#3b82f6', letterSpacing: '2px', textTransform: 'uppercase' }}>Apoie o Ministério</span>
          <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: '12px 0 12px', letterSpacing: '-0.5px' }}>
            Contribuições
          </h2>
          <p style={{ fontSize: '15px', color: '#64748b', margin: '0 0 40px', lineHeight: '1.7' }}>
            Sua contribuição sustenta o ministério e ajuda a impactar mais vidas.
          </p>

          <div style={{ background: '#f8fafc', borderRadius: '20px', padding: '32px', border: '1px solid #e2e8f0', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Heart size={17} style={{ color: '#3b82f6' }} />
              </div>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>PIX / Transferência</span>
            </div>

            {pixInfo.map(({ label, value }, i) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: i < pixInfo.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600', minWidth: '100px', flexShrink: 0 }}>{label}</span>
                <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: '700', textAlign: 'right' }}>{value}</span>
              </div>
            ))}

            <button
              onClick={copyPix}
              className="btn-hover"
              style={{
                width: '100%', marginTop: '24px', padding: '13px',
                background: copied ? '#059669' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: '#ffffff', border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'background 0.3s',
                boxShadow: copied ? '0 4px 12px rgba(5,150,105,0.3)' : '0 4px 12px rgba(59,130,246,0.3)',
              }}
            >
              {copied ? <><Check size={15} /> CNPJ Copiado!</> : <><Copy size={15} /> Copiar CNPJ PIX</>}
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: '#0f172a', color: 'rgba(255,255,255,0.5)', padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
            <img src="/apas.png" alt="Apascentar" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', opacity: 0.85 }} />
            <span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '700', fontSize: '15px' }}>Comunidade Evangélica Apascentar de Curitiba</span>
          </div>
          <p style={{ fontSize: '13px', margin: '0 0 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <MapPin size={12} />
            Rua Atílio Brunetti, 2014 — Capão Raso, Curitiba - PR
          </p>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '20px', textAlign: 'center' }}>
            <span style={{ fontSize: '12px' }}>© 2025 Comunidade Evangélica Apascentar de Curitiba</span>
          </div>
        </div>
      </footer>

      {/* MODAL PEDIDO DE ORAÇÃO */}
      {showOracaoModal && (
        <div onClick={() => { setShowOracaoModal(false); setOracaoStatus(null); }} style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#ffffff', borderRadius: '20px',
            padding: '32px', width: '100%', maxWidth: '480px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
          }}>
            {oracaoStatus === 'success' ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: '52px', marginBottom: '16px' }}>🙏</div>
                <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b', margin: '0 0 8px' }}>
                  Pedido enviado!
                </h2>
                <p style={{ fontSize: '15px', color: '#64748b', margin: 0 }}>
                  Vamos orar por você. Deus te abençoe!
                </p>
              </div>
            ) : (
              <form onSubmit={handleEnviarOracao}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
                    🙏 Pedido de Oração
                  </h2>
                  <button type="button" onClick={() => { setShowOracaoModal(false); setOracaoStatus(null); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#94a3b8', lineHeight: 1 }}>
                    ×
                  </button>
                </div>
                <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px', lineHeight: 1.6 }}>
                  Compartilhe seu pedido. Nossa equipe vai interceder por você em oração.
                </p>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Seu nome <span style={{ color: '#94a3b8', fontWeight: 400 }}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Como podemos te chamar?"
                    value={oracaoForm.nome}
                    onChange={e => setOracaoForm(p => ({ ...p, nome: e.target.value }))}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: '10px',
                      border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none',
                      boxSizing: 'border-box', fontFamily: 'inherit',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Seu pedido *
                  </label>
                  <textarea
                    required
                    placeholder="Escreva seu pedido de oração aqui..."
                    value={oracaoForm.pedido}
                    onChange={e => setOracaoForm(p => ({ ...p, pedido: e.target.value }))}
                    rows={5}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: '10px',
                      border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none',
                      resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                    }}
                  />
                </div>
                {oracaoStatus === 'error' && (
                  <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 12px' }}>
                    Erro ao enviar. Tente novamente.
                  </p>
                )}
                <button type="submit" disabled={oracaoStatus === 'sending'} style={{
                  width: '100%', padding: '13px',
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  color: '#ffffff', border: 'none', borderRadius: '10px',
                  fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
                }}>
                  {oracaoStatus === 'sending' ? 'Enviando...' : '🙏 Enviar Pedido'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <style>{`
        html { scroll-behavior: smooth; }
        .hover-lift { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .hover-lift:hover { transform: translateY(-6px) scale(1.02); box-shadow: 0 16px 40px rgba(0,0,0,0.18) !important; }
        @keyframes bounce-soft { 0%, 100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(8px); } }
        .scroll-bounce { animation: bounce-soft 2.2s ease-in-out infinite; }
        .btn-hover { transition: transform 0.2s ease, filter 0.2s ease; }
        .btn-hover:hover { transform: translateY(-2px); filter: brightness(1.06); }
        @media (max-width: 768px) {
          .meetings-grid { grid-template-columns: 1fr !important; }
          .location-grid { grid-template-columns: 1fr !important; }
          .sobre-grid { grid-template-columns: 1fr !important; }
          .gallery-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .nav-link-hide { display: none !important; }
        }
        @media (min-width: 480px) and (max-width: 768px) {
          .meetings-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
